/**
 * The pictures beside a walkthrough step (doc 3 E4, E15; addendum R3): all ten, drawn as
 * Anthony's prototype draws them (Sign-up Screens, Prototype p0-p9), every word from the
 * shared WALKTHROUGH_PICTURE_COPY so mobile can't drift (rule 17).
 *
 * They show his sample figures, not the user's: the walkthrough opens before there is an
 * account (the splash page, sign-up), and the same pictures must read the same
 * everywhere. No "Example" label and no tab bar: his design has neither (Paul, N8, N14).
 * Colours are the app's tokens for his hexes (#2373E2 primary, #C77700 warning, #1F6B45
 * success, the navy panels a walkthrough token), so dark mode holds. Decoration to a screen reader: the step's words carry the meaning.
 */
import { Check } from "lucide-react";
import type { ReactNode } from "react";
import { WALKTHROUGH_PICTURE_COPY as COPY } from "@fomofi/shared";
import { MONEY_LABEL as MONEY_LABEL_BASE, MONEY_ROW } from "@/lib/moneyRow";
import { cn } from "@/lib/utils";

/** A row's label keeps at least its longest word, so on a narrow phone at 200% text the
 *  figure wraps under it instead of overlapping a one-word name ("Transportation"). */
const MONEY_LABEL = `${MONEY_LABEL_BASE} @max-[14rem]:min-w-fit`;

function Frame({ children, gap = "gap-2.5" }: { children: ReactNode; gap?: string }) {
  return (
    <div className={cn("@container flex w-full min-w-0 flex-col", gap)} aria-hidden>
      {children}
    </div>
  );
}

/** A white row: name on the left, figure on the right; they stack when squeezed. */
function Row({
  name,
  detail,
  className,
  nameClass,
  detailClass = "font-semibold",
}: {
  name: string;
  detail: string;
  className?: string;
  nameClass?: string;
  detailClass?: string;
}) {
  return (
    <div className={cn(MONEY_ROW, "items-center gap-2 bg-background text-sm", className)}>
      <span className={cn(MONEY_LABEL, nameClass)}>{name}</span>
      <span className={cn("shrink-0 tabular-nums", detailClass)}>{detail}</span>
    </div>
  );
}

/** A thin bar, `pct` filled (the prototype's widths). */
function Bar({ pct, fill = "bg-primary" }: { pct: number; fill?: string }) {
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
      <div className={cn("h-2 rounded-full", fill)} style={{ width: `${pct}%` }} />
    </div>
  );
}

/** p0: the bank search, three tiles (the middle one picked), the connected account. */
function ConnectBankPicture() {
  return (
    <Frame>
      <div className="rounded-[10px] border border-field-border bg-background px-3 py-2.5 text-[13px] text-muted-foreground">
        {COPY.connectBank.search.text}
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div className="h-11 rounded-[10px] border border-border bg-background" />
        <div className="h-11 rounded-[10px] border-2 border-primary bg-background" />
        <div className="h-11 rounded-[10px] border border-border bg-background" />
      </div>
      <div
        className={cn(MONEY_ROW, "items-center gap-2 rounded-xl bg-background px-3.5 py-3 text-sm")}
      >
        <span className={cn(MONEY_LABEL, "font-semibold tabular-nums")}>
          {COPY.connectBank.account.name}
        </span>
        <span className="inline-flex items-center gap-1 font-semibold text-success">
          {COPY.connectBank.account.detail} <Check className="h-4 w-4" />
        </span>
      </div>
    </Frame>
  );
}

/** p1: the finished review, then what it found. */
function AnalysisPicture() {
  const { progress, rows } = COPY.analysis;
  return (
    <Frame>
      <div className={cn(MONEY_ROW, "gap-2 text-[13px] font-bold")}>
        <span className={MONEY_LABEL}>{progress.name}</span>
        <span className="shrink-0 tabular-nums">{progress.detail}</span>
      </div>
      <div className="h-2.5 rounded-full bg-primary" />
      {rows.map((r) => (
        <Row
          key={r.name}
          name={r.name}
          detail={r.detail}
          className="rounded-xl px-3.5 py-2.5"
          detailClass="font-semibold text-success"
        />
      ))}
    </Frame>
  );
}

/** p2: the paycheck we found, with Looks right / Edit. */
function IncomePicture() {
  const { paycheck, confirm, edit } = COPY.income;
  return (
    <Frame>
      <div className="flex flex-col gap-1 rounded-[14px] bg-background px-4 py-3.5">
        <span className="text-xs text-muted-foreground">{paycheck.label}</span>
        <span className="text-2xl font-bold tabular-nums">{paycheck.text}</span>
        <span className="text-[13px] text-muted-foreground">{paycheck.detail}</span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <span className="flex min-h-10 items-center justify-center rounded-[10px] bg-primary-fill px-2 text-center text-sm font-semibold text-primary-fill-foreground">
          {confirm.label}
        </span>
        <span className="flex min-h-10 items-center justify-center rounded-[10px] border border-field-border bg-background px-2 text-center text-sm">
          {edit.label}
        </span>
      </div>
    </Frame>
  );
}

/** p3: balance, minus the bills before payday, is the remaining balance. One grid, so
 *  the bars line up and widen with the text instead of clipping it. */
function RemainingPicture() {
  const [balance, bills, remaining] = COPY.remaining.rows;
  return (
    <Frame>
      <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-2 gap-y-2.5 rounded-[14px] bg-walkthrough-panel p-4 text-[13px] text-walkthrough-panel-foreground">
        <span>{balance.name}</span>
        <div className="h-3 rounded bg-walkthrough-balance" />
        <span className="text-right font-bold tabular-nums">{balance.detail}</span>
        <span>{bills.name}</span>
        <div className="h-3">
          <div className="h-3 rounded bg-walkthrough-bills" style={{ marginLeft: "93%" }} />
        </div>
        <span className="text-right font-bold tabular-nums">{bills.detail}</span>
        <div className="col-span-3 border-t border-walkthrough-panel-line" />
        <span className="font-bold">{remaining.name}</span>
        <div className="h-3 rounded bg-walkthrough-panel-foreground" style={{ width: "93%" }} />
        <span className="text-right font-bold tabular-nums">{remaining.detail}</span>
      </div>
    </Frame>
  );
}

/** p4: the remaining balance split into ticked buckets. */
function CreateBucketsPicture() {
  return (
    <Frame gap="gap-1.5">
      <p className="mb-0.5 text-[13px] font-semibold text-primary">{COPY.buckets.remaining.text}</p>
      <ul className="flex flex-col gap-1.5">
        {COPY.buckets.rows.map(({ name, detail }) => (
          <li
            key={name}
            className={cn(
              MONEY_ROW,
              "items-center gap-2 rounded-[10px] bg-background px-[12px] py-2 text-sm",
            )}
          >
            <span className={cn(MONEY_LABEL, "flex min-w-0 items-center gap-2")}>
              <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded bg-primary-fill text-primary-fill-foreground">
                <Check className="h-3 w-3" strokeWidth={3} />
              </span>
              <span className="min-w-0 hyphens-auto [overflow-wrap:anywhere]" lang="en">
                {name}
              </span>
            </span>
            <span className="shrink-0 font-bold tabular-nums">{detail}</span>
          </li>
        ))}
      </ul>
    </Frame>
  );
}

/** How far each example debt is paid off, in the order of COPY.debt.rows (p5). */
const DEBT_BAR_PCT = [38, 12] as const;

/** p5: what the analysis found, the card paid first, the next, and the debt-free date. */
function DebtPicture() {
  return (
    <Frame>
      <p className="text-[13px] font-semibold text-primary [overflow-wrap:anywhere]">
        {COPY.debt.found.text}
      </p>
      {COPY.debt.rows.map(({ name, detail }, i) => (
        <div key={name} className="flex flex-col gap-1.5 rounded-xl bg-background px-3.5 py-3">
          <div className={cn(MONEY_ROW, "items-center gap-2 text-sm")}>
            <span className={cn(MONEY_LABEL, "font-bold")}>{name}</span>
            <span>{detail}</span>
          </div>
          <Bar pct={DEBT_BAR_PCT[i]} fill="bg-warning" />
        </div>
      ))}
      <p className="rounded-xl bg-warning/15 px-3.5 py-2.5 text-sm font-semibold text-foreground">
        {COPY.debt.plan.text}
      </p>
    </Frame>
  );
}

/** p6: the payday check-in. */
function CheckinPicture() {
  const { title, paycheck, leftovers } = COPY.checkin;
  return (
    <Frame>
      <div className="flex flex-col gap-2.5 rounded-[14px] bg-background p-4">
        <span className="text-[13px] font-semibold text-primary">{title.text}</span>
        <div className={cn(MONEY_ROW, "items-center gap-2 rounded-xl bg-primary/10 px-3.5 py-3")}>
          <span className={cn(MONEY_LABEL, "text-[22px] font-bold tabular-nums")}>
            {paycheck.name}
          </span>
          <span className="shrink-0 text-xs font-semibold text-success">{paycheck.detail}</span>
        </div>
        <span className="text-[13px] text-foreground">{leftovers.text}</span>
      </div>
    </Frame>
  );
}

/** p7: Home's Safe to spend, and a bucket's money left. */
function HomePicture() {
  const { safe, bucket } = COPY.home;
  return (
    <Frame>
      <div className="rounded-[14px] bg-walkthrough-panel p-4 text-walkthrough-panel-foreground">
        <div className="text-xs text-walkthrough-panel-muted">{safe.label}</div>
        <div className="text-[34px] leading-tight font-bold tabular-nums">{safe.text}</div>
      </div>
      <div className="flex flex-col gap-1.5 rounded-xl bg-background px-3.5 py-2.5 text-[13px]">
        <Row
          name={bucket.name}
          detail={bucket.detail}
          className="text-[13px]"
          detailClass="font-bold"
        />
        <Bar pct={54} />
      </div>
    </Frame>
  );
}

/** p8: the bills and the paycheck before payday. */
function UpcomingPicture() {
  const rows = COPY.upcoming.rows;
  return (
    <Frame gap="gap-1.5">
      {rows.map((r, i) => {
        const paycheck = i === rows.length - 1;
        return (
          <Row
            key={r.name}
            name={r.name}
            detail={r.detail}
            className={cn("rounded-[10px] px-3 py-2", paycheck && "bg-success/10")}
            detailClass={cn("font-bold", paycheck && "text-success")}
          />
        );
      })}
    </Frame>
  );
}

/** Each bucket's month in p9: how full its bar is; Splash Fund went over. */
const INSIGHTS_BARS = [
  { pct: 95, over: false },
  { pct: 100, over: true },
  { pct: 87, over: false },
] as const;

/** p9: last month, plan against actual. */
function InsightsPicture() {
  const { title, rows } = COPY.insights;
  return (
    <Frame>
      <div className="flex flex-col gap-2.5 rounded-[14px] bg-background px-4 py-3.5 text-[13px]">
        <span className="text-sm font-bold">{title.text}</span>
        {rows.map((r, i) => {
          const { pct, over } = INSIGHTS_BARS[i];
          return (
            <div key={r.name} className="flex flex-col gap-1">
              <div className={cn(MONEY_ROW, "gap-2")}>
                <span className={MONEY_LABEL}>{r.name}</span>
                <span className={cn("shrink-0 tabular-nums", over && "font-semibold text-warning")}>
                  {r.detail}
                </span>
              </div>
              <Bar pct={pct} fill={over ? "bg-warning" : "bg-primary"} />
            </div>
          );
        })}
      </div>
    </Frame>
  );
}

const PICTURES = [
  ConnectBankPicture,
  AnalysisPicture,
  IncomePicture,
  RemainingPicture,
  CreateBucketsPicture,
  DebtPicture,
  CheckinPicture,
  HomePicture,
  UpcomingPicture,
  InsightsPicture,
] as const;

/** The picture for a step (1-10). */
export function WalkthroughPicture({ step }: { step: number }) {
  const Picture = PICTURES[step - 1];
  return Picture ? <Picture /> : null;
}
